import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, ScanSearch, FileDown, Undo2, Link2 } from "lucide-react";
import { toast } from "sonner";

const csv = (rows) => {
  const head = ["Category ID", "Category Name", "Old URL", "New URL", "Redirect Status", "Product Count", "SEO Validation"];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const body = rows.map((r) => [r.id, r.name, r.old_url, r.new_url, r.redirect, r.product_count, r.seo].map(esc).join(","));
  const blob = new Blob([[head.join(","), ...body].join("\n")], { type: "text/csv" });
  const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "category-migration-report.csv"; a.click();
};

export function CategorySlugTools({ coll, categories, onChanged }) {
  const [audit, setAudit] = useState(null);
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState("");
  const [migs, setMigs] = useState(null);
  const [unmapped, setUnmapped] = useState(null);

  const run = async (k, fn) => { setBusy(k); try { await fn(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(""); } };
  const doAudit = () => run("audit", async () => { const r = await api.get("/admin/category-slugs/audit", { params: { coll } }); setAudit(r.data); setPicked([]); });
  const fix = () => run("fix", async () => { const r = await api.post("/admin/category-slugs/fix", { ids: picked }); toast.success(`${r.data.changed.length} slug(s) corrected — old URLs redirect`); doAudit(); onChanged(); });
  const report = () => run("report", async () => { const r = await api.get("/admin/category-slugs/report"); csv(r.data.items); });
  const loadMigs = () => run("migs", async () => { const r = await api.get("/admin/category-slugs/migrations"); setMigs(r.data.items); });
  const rollback = (id) => run("rb", async () => { const r = await api.post(`/admin/category-slugs/migrations/${id}/rollback`); toast.success(`Rolled back ${r.data.slugs_restored} slug(s)`); loadMigs(); onChanged(); });
  const loadUnmapped = () => run("um", async () => { const r = await api.get("/admin/category-slugs/unmapped"); setUnmapped(r.data.items); });
  const map = (raw, cid) => run("map", async () => { const r = await api.post("/admin/category-slugs/map", { raw, category_id: cid }); toast.success(`${r.data.products_linked} product(s) mapped to ${r.data.category}`); loadUnmapped(); onChanged(); });

  return (
    <div className="rounded-xl border bg-white p-4 mb-4 space-y-3" data-testid="category-slug-tools">
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={doAudit} disabled={!!busy} data-testid="slug-audit-run">
          {busy === "audit" ? <Loader2 size={14} className="mr-1 animate-spin" /> : <ScanSearch size={14} className="mr-1" />} Bulk slug audit
        </Button>
        {coll === "categories" && <>
          <Button size="sm" variant="outline" onClick={report} disabled={!!busy} data-testid="slug-report-csv"><FileDown size={14} className="mr-1" /> Migration report (CSV)</Button>
          <Button size="sm" variant="outline" onClick={loadMigs} disabled={!!busy} data-testid="slug-migrations-load"><Undo2 size={14} className="mr-1" /> Migrations &amp; rollback</Button>
          <Button size="sm" variant="outline" onClick={loadUnmapped} disabled={!!busy} data-testid="slug-unmapped-load"><Link2 size={14} className="mr-1" /> Supplier category mapping</Button>
        </>}
      </div>

      {audit && (
        <div data-testid="slug-audit-result" className="text-sm">
          <p className="text-steel">{audit.rows.length} of {audit.total} {coll} need attention · products with malformed URLs: <b>{audit.products_with_malformed_slugs}</b></p>
          {coll === "brands" && audit.rows.length > 0 && <p className="text-xs text-muted-foreground">Brand pages keep their current URLs; this audit is informational.</p>}
          {audit.rows.length > 0 && (
            <div className="mt-2 rounded-lg border divide-y max-h-64 overflow-y-auto">
              {audit.rows.map((r) => (
                <label key={r.id} className="flex items-center gap-3 p-2 text-xs">
                  {coll === "categories" && <Checkbox checked={picked.includes(r.id)} onCheckedChange={() => setPicked((p) => p.includes(r.id) ? p.filter((x) => x !== r.id) : [...p, r.id])} data-testid={`slug-audit-pick-${r.id}`} />}
                  <span className="flex-1"><b>{r.name}</b> <span className="font-mono">{r.slug || "(none)"} → {r.proposed}</span> <span className="text-amber-700">{r.issues.join(", ")}</span></span>
                </label>
              ))}
            </div>
          )}
          {coll === "categories" && audit.rows.length > 0 && (
            <Button size="sm" className="mt-2 bg-accent text-steel hover:bg-amber-600 font-semibold" disabled={!picked.length || !!busy} onClick={fix} data-testid="slug-fix-apply">Approve {picked.length} correction(s)</Button>
          )}
        </div>
      )}

      {migs && (
        <div className="rounded-lg border divide-y text-xs" data-testid="slug-migrations">
          {migs.length === 0 ? <p className="p-2 text-muted-foreground">No migrations recorded.</p> : migs.map((m) => (
            <div key={m.id} className="p-2 flex items-center justify-between">
              <span>{new Date(m.at).toLocaleString()} · {m.by} · {m.slug_changes.length} slug change(s){m.rolled_back ? " · rolled back" : ""}</span>
              {!m.rolled_back && <Button size="sm" variant="outline" className="h-7" onClick={() => rollback(m.id)} data-testid={`slug-migration-rollback-${m.id}`}>Rollback</Button>}
            </div>))}
        </div>
      )}

      {unmapped && (
        <div className="rounded-lg border divide-y text-xs max-h-64 overflow-y-auto" data-testid="slug-unmapped">
          {unmapped.length === 0 ? <p className="p-2 text-emerald-600">Every supplier category maps to a managed category.</p> : unmapped.map((u) => (
            <div key={u.raw} className="p-2 flex items-center justify-between gap-2">
              <span><b>{u.raw}</b> · {u.count} product(s)</span>
              <select defaultValue="" onChange={(e) => e.target.value && map(u.raw, e.target.value)} className="h-8 rounded-md border px-2" data-testid={`slug-map-${u.raw}`}>
                <option value="">Map to…</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>))}
        </div>
      )}
    </div>
  );
}
